import { useState } from 'react';
import html2canvas from 'html2canvas';

interface RiskCardProps {
    tokenName: string;
    tokenSymbol: string;
    tokenAddress: string;
    riskScore: number;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    creatorTokenCount: number;
    top5HolderPercent: number;
    bundledBuys: boolean;
}

export function RiskCardDownloader({
    tokenName,
    tokenSymbol,
    tokenAddress,
    riskScore,
    riskLevel,
    creatorTokenCount,
    top5HolderPercent,
    bundledBuys
}: RiskCardProps) {
    const [isGenerating, setIsGenerating] = useState(false);

    const getRiskColor = () => {
        switch (riskLevel) {
            case 'low': return '#00FF88';
            case 'medium': return '#FFB800';
            case 'high': return '#FF8C00';
            case 'critical': return '#FF3B3B';
            default: return '#888';
        }
    };

    const getRiskEmoji = () => {
        switch (riskLevel) {
            case 'low': return '🟢';
            case 'medium': return '🟡';
            case 'high': return '🟠';
            case 'critical': return '🔴';
            default: return '⚪';
        }
    };

    const handleDownload = async () => {
        setIsGenerating(true);

        try {
            const cardElement = document.getElementById('risk-card-canvas');
            if (!cardElement) {
                console.error('Card element not found');
                return;
            }

            // Generate canvas from the element
            const canvas = await html2canvas(cardElement, {
                backgroundColor: '#0a0a0a',
                scale: 2, // Higher quality
                logging: false,
            });

            // Convert to blob and download
            canvas.toBlob((blob) => {
                if (!blob) return;

                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `skop-${tokenSymbol.toLowerCase()}-risk-card.png`;
                link.click();
                URL.revokeObjectURL(url);
            });
        } catch (error) {
            console.error('Failed to generate card:', error);
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div>
            {/* Hidden card for canvas generation */}
            <div
                id="risk-card-canvas"
                style={{
                    position: 'absolute',
                    left: '-9999px',
                    width: '600px',
                    padding: '32px',
                    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
                    borderRadius: '16px',
                    border: `2px solid ${getRiskColor()}`,
                    fontFamily: 'Inter, system-ui, sans-serif',
                }}
            >
                {/* Header */}
                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                    <div style={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#00FF88',
                        letterSpacing: '2px',
                        marginBottom: '8px',
                    }}>
                        SKŌP
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        Skop it before you ape
                    </div>
                </div>

                {/* Token Info */}
                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}>
                        {tokenName}
                    </div>
                    <div style={{ fontSize: '14px', color: '#888' }}>
                        {tokenSymbol}
                    </div>
                </div>

                {/* Risk Score Circle */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: '24px',
                }}>
                    <div style={{
                        width: '180px',
                        height: '180px',
                        borderRadius: '50%',
                        border: `6px solid ${getRiskColor()}`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: `${getRiskColor()}15`,
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '8px' }}>
                            {getRiskEmoji()}
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: 700, color: getRiskColor() }}>
                            {riskScore}
                        </div>
                        <div style={{ fontSize: '14px', color: '#888' }}>
                            / 100
                        </div>
                    </div>
                </div>

                {/* Risk Level Badge */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div style={{
                        display: 'inline-block',
                        padding: '8px 24px',
                        background: getRiskColor(),
                        color: '#000',
                        fontSize: '16px',
                        fontWeight: 700,
                        borderRadius: '8px',
                        textTransform: 'uppercase',
                    }}>
                        {riskLevel} RISK
                    </div>
                </div>

                {/* Stats Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '12px',
                    marginBottom: '24px',
                }}>
                    <div style={{
                        padding: '12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #333',
                    }}>
                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>
                            CREATOR TOKENS
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: creatorTokenCount > 5 ? '#FFB800' : '#fff',
                        }}>
                            {creatorTokenCount}
                        </div>
                    </div>
                    <div style={{
                        padding: '12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #333',
                    }}>
                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>
                            TOP 5 HOLD
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: top5HolderPercent > 50 ? '#FF3B3B' : '#fff',
                        }}>
                            {top5HolderPercent.toFixed(0)}%
                        </div>
                    </div>
                    <div style={{
                        padding: '12px',
                        background: '#1a1a1a',
                        borderRadius: '8px',
                        border: '1px solid #333',
                    }}>
                        <div style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>
                            BUNDLED
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: 700,
                            color: bundledBuys ? '#FFB800' : '#00FF88',
                        }}>
                            {bundledBuys ? 'YES' : 'NO'}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    textAlign: 'center',
                    fontSize: '11px',
                    color: '#666',
                    fontFamily: 'monospace',
                }}>
                    skop.site • {tokenAddress.slice(0, 8)}...{tokenAddress.slice(-6)}
                </div>
            </div>

            {/* Download Button */}
            <button
                onClick={handleDownload}
                disabled={isGenerating}
                style={{
                    width: '100%',
                    padding: '12px',
                    background: isGenerating ? '#333' : 'var(--accent)',
                    color: isGenerating ? '#888' : '#000',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                }}
            >
                {isGenerating ? (
                    <>
                        <div className="animate-spin" style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #666',
                            borderTopColor: '#888',
                            borderRadius: '50%',
                        }} />
                        Generating...
                    </>
                ) : (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download Risk Card
                    </>
                )}
            </button>
        </div>
    );
}
